import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import "./order.css";
import { FaCopy, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import axiosInstance from "../../../helpers/axiosInstance";

// Create a reusable OrderList component
const OrderList = ({
  orders = [],
  selectedOrders = [],
  handleCheckboxChange = () => {},
  initialLoading = false,
  refreshing = false,
  showCheckbox = true,
  showAddress = true,
  showViewButton = true,
  customBackground = null,
  customRender = null,
  className = "",
  pagination = null,
  onPageChange = () => {},
  handleStatusUpdate = null,
  setSelectedOrders = () => {},
}) => {
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setFullscreenImageUrl(null);
    };
    if (fullscreenImageUrl) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [fullscreenImageUrl]);
  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status, rejectStatus) => {
    // First check for reject status if provided
    if (
      (status === "rejected" ||
        status === "cancelled" ||
        status === "paymentpending") &&
      rejectStatus
    ) {
      switch (rejectStatus.toLowerCase()) {
        case "resolved":
          return "success";
        case "pending":
          return "warning";
        default:
          return "secondary";
      }
    }

    // Then check main status
    switch (status) {
      case "processing":
        return "#FFBF00";
      case "delivered":
        return "success";
      case "shipped":
        return "info";
      case "outForDelivery":
        return "primary";
      case "rejected":
      case "cancelled":
      case "paymentpending":
        return "danger"; // These statuses will show a red badge
      default:
        return "warning";
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this order?")) {
      try {
        await axiosInstance.delete(`/api/v1/admin/orders/${id}`);
        toast.success("Order deleted successfully");
        window.location.reload();
      } catch (error) {
        toast.error("Error deleting order");
        console.error("Error deleting order:", error);
      }
    }
  };
  return (
    <div className={`order-list-container ${className}`}>
      {initialLoading ? (
        <div className="text-center my-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          {refreshing && (
            <div className="text-center my-2">
              <Spinner size="sm" color="primary" />
            </div>
          )}
          {showCheckbox && (
            <div className="select-all-container mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="selectAll"
                  checked={
                    selectedOrders.length > 0 &&
                    selectedOrders.length === orders.length
                  }
                  onChange={(e) => {
                    if (e.target.checked) {
                      console.log("orders checked", orders);
                      setSelectedOrders(orders.map((order) => order._id));
                    } else {
                      console.log("orders unchecked", orders);
                      setSelectedOrders([]);
                    }
                  }}
                />
                <label className="form-check-label" htmlFor="selectAll">
                  Select All Orders
                </label>
              </div>
            </div>
          )}
          <div className="order-list">
            {orders.map((order) => (
              <div key={order._id} className="order-card">
                {/* Header Section with Background Color */}
                <div
                  style={{
                    backgroundColor: customBackground
                      ? customBackground(order)
                      : order.shippingAddress?.postalCode === "132103"
                      ? "#00ab79"
                      : "#158abc",
                    padding: "16px",
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                  }}
                >
                  <div className="order-card-header">
                    {showCheckbox && (
                      <div style={{ display: "flex", gap: "4px" }}>
                        <input
                          type="checkbox"
                          checked={selectedOrders.includes(order._id)}
                          onChange={() =>
                            handleCheckboxChange &&
                            handleCheckboxChange(order._id)
                          }
                          style={{
                            cursor: "pointer",
                            height: "22px",
                            width: "22px",
                          }}
                        />

                        <h4
                          style={{
                            color: "#fff",
                            margin: 0,
                            fontSize: "16px",
                            fontWeight: "600",
                          }}
                        >
                          Order #{order._id}
                        </h4>
                      </div>
                    )}

                    {!showCheckbox && (
                      <h4
                        style={{
                          color: "#fff",
                          margin: 0,
                          fontSize: "16px",
                          fontWeight: "600",
                        }}
                      >
                        Order #{order._id}
                      </h4>
                    )}

                    <p
                      style={{
                        margin: "8px 0",
                        fontSize: "16px",
                        fontWeight: "bold",
                        color: "#ffff",
                        textTransform: "capitalize",
                      }}
                    >
                      {order.shippingAddress?.name}
                    </p>

                    {/* Add dropdown menu for status */}
                    {handleStatusUpdate && (
                      <div className="status-dropdown">
                        <UncontrolledDropdown>
                          <DropdownToggle tag="span">
                            <Badge
                              color={getStatusColor(
                                order.status,
                                order.rejectStatus
                              )}
                              style={{
                                fontSize: "14px",
                                padding: "6px 12px",
                                cursor: "pointer",
                              }}
                            >
                              {order.status === "outForDelivery"
                                ? "Dispatched"
                                : order.status || "N/A"}
                              {(order.status === "rejected" ||
                                order.status === "paymentpending" ||
                                order.status === "cancelled") && (
                                <Badge
                                  color={getStatusColor(
                                    order.status,
                                    order.rejectStatus || "pending"
                                  )}
                                  className="ms-1"
                                  pill
                                  style={{
                                    fontSize: "0.7em",
                                    padding: "0.2em 0.6em",
                                  }}
                                >
                                  {order.rejectStatus || "pending"}
                                </Badge>
                              )}
                            </Badge>
                          </DropdownToggle>
                          <DropdownMenu>
                            {order.status === "rejected" ||
                            order.status === "paymentpending" ||
                            order.status === "cancelled" ? (
                              <>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(
                                      order._id,
                                      order.status,
                                      "resolved"
                                    )
                                  }
                                  className="d-flex align-items-center"
                                  active={order.rejectStatus === "resolved"}
                                >
                                  <i className="ri-check-line me-2 text-success"></i>
                                  {order.rejectStatus === "resolved"
                                    ? "✓ Marked as Resolved"
                                    : "Mark as Resolved"}
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(
                                      order._id,
                                      order.status,
                                      "pending"
                                    )
                                  }
                                  className="d-flex align-items-center"
                                  active={
                                    !order.rejectStatus ||
                                    order.rejectStatus === "pending"
                                  }
                                >
                                  <i className="ri-time-line me-2 text-warning"></i>
                                  {!order.rejectStatus ||
                                  order.rejectStatus === "pending"
                                    ? "✓ Marked as Pending"
                                    : "Mark as Pending"}
                                </DropdownItem>
                                <DropdownItem divider />
                              </>
                            ) : (
                              <>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "pending")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-time-line me-2 text-warning"></i>
                                  Pending
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "accepted")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-check-double-line me-2 text-success"></i>
                                  Accepted
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "printed")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-printer-line me-2 text-info"></i>
                                  Printed
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(
                                      order._id,
                                      "outForDelivery"
                                    )
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-truck-line me-2 text-primary"></i>
                                  Dispatched
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "delivered")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-checkbox-circle-line me-2 text-success"></i>
                                  Delivered
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "rejected")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-close-circle-line me-2 text-danger"></i>
                                  Rejected
                                </DropdownItem>
                                <DropdownItem
                                  onClick={() =>
                                    handleStatusUpdate(order._id, "cancelled")
                                  }
                                  className="d-flex align-items-center"
                                >
                                  <i className="ri-delete-bin-line me-2 text-danger"></i>
                                  Cancelled
                                </DropdownItem>
                              </>
                            )}
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    )}

                    <div className="status-dropdown">
                      <Badge
                        color={
                          order.paymentMethod === "cod" ? "warning" : "success"
                        }
                        style={{
                          fontSize: "14px",
                          padding: "6px 12px",
                          cursor: "pointer",
                        }}
                      >
                        <Badge
                          color={
                            order.paymentMethod === "cod"
                              ? "warning"
                              : "success"
                          }
                          className="ms-1"
                          pill
                          style={{ fontSize: "0.7em", padding: "0.2em 0.6em" }}
                        >
                          {order.paymentMethod === "cod"
                            ? "Cash on Delivery"
                            : "Online Payment"}
                        </Badge>
                      </Badge>
                    </div>

                    <p
                      style={{
                        margin: "8px 0",
                        fontSize: "14px",
                        color: "#fff",
                      }}
                    >
                      <strong>Date:</strong> {formatDate(order.createdAt)}
                    </p>

                    <p
                      style={{
                        margin: "8px 0",
                        fontWeight: "bold",
                        color: "#ffff",
                        fontSize: "18px",
                      }}
                    >
                      <strong></strong> {formatCurrency(order.totalAmount)}
                    </p>

                    {showViewButton && (
                      <Link
                        to={`/order-details/${order._id}`}
                        className="btn btn-success"
                        target="_blank"
                        style={{
                          padding: "8px 16px",
                          borderRadius: "6px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <img
                          style={{ height: "16px", width: "16px" }}
                          src="/view.png"
                          alt="View"
                        />
                      </Link>
                    )}
                    <div
                      className="btn btn-success"
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                        backgroundColor: "red",
                      }}
                      onClick={() => {
                        handleDelete(order._id);
                      }}
                    >
                      <FaTrash />
                    </div>
                    <div
                      className="btn btn-success"
                      style={{
                        padding: "8px 16px",
                        borderRadius: "6px",
                        fontSize: "14px",
                        fontWeight: "500",
                      }}
                      onClick={() => {
                        const orderData = `
                          Order ID: ${order._id}
                          Customer Name: ${order.shippingAddress?.name}
                          Address: ${order.shippingAddress?.street}, ${
                          order.shippingAddress?.city
                        }, ${order.shippingAddress?.state} - ${
                          order.shippingAddress?.postalCode
                        }
                          Phone: ${
                            order.shippingAddress?.phone || order.user?.phone
                          }
                          Email: ${order.shippingAddress?.email}
                          Total Amount: ${formatCurrency(order.totalAmount)}
                          Date: ${formatDate(order.createdAt)}
                        `;
                        navigator.clipboard.writeText(orderData).then(() => {
                          toast.success("Order details copied to clipboard");
                        });
                      }}
                    >
                      <FaCopy />
                    </div>
                  </div>

                  {showAddress && (
                    <div
                      className="justify-between address-container"
                      style={{
                        display: "flex",
                        width: "100%",
                        justifyContent: "space-between",
                        marginLeft: "30px",
                      }}
                    >
                      <p
                        style={{
                          margin: "8px 0",
                          fontSize: "16px",
                          color: "#ffff",
                        }}
                      >
                        <strong>Address:</strong>{" "}
                        {order.shippingAddress?.street},{" "}
                        {order.shippingAddress?.city},{" "}
                        {order.shippingAddress?.state} -{" "}
                        {order.shippingAddress?.postalCode} | (M)
                        {order.shippingAddress?.phone || order.user?.phone} |
                        (✉){order.shippingAddress?.email}
                      </p>
                    </div>
                  )}
                </div>

                {/* Body Section */}
                <div className="order-card-body" style={{ padding: "16px" }}>
                  <div
                    className="justify-between"
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  ></div>

                  {/* Product List */}
                  <div className="product-list">
                    {customRender ? (
                      customRender(order)
                    ) : (
                      <>
                        {order.orders &&
                          order.orders.map((item) => (
                            <div
                              key={item._id}
                              className="product-item"
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "12px",
                                border: "1px solid #eee",
                                borderRadius: "8px",
                                marginBottom: "12px",
                              }}
                            >
                              <img
                                src={
                                  item.product.images &&
                                  item.product.images.length > 0
                                    ? item.product.images[0].startsWith("https")
                                      ? item.product.images[0]
                                      : `${process.env.REACT_APP_BASEURL}${item.product.images[0]}`
                                    : ""
                                }
                                alt={item.product.name}
                                className="product-image"
                                style={{
                                  width: "60px",
                                  height: "60px",
                                  objectFit: "cover",
                                  borderRadius: "8px",
                                cursor: "zoom-in",
                                }}
                              onClick={() =>
                                setFullscreenImageUrl(
                                  item.product.images &&
                                    item.product.images.length > 0
                                    ? item.product.images[0].startsWith("https")
                                      ? item.product.images[0]
                                      : `${process.env.REACT_APP_BASEURL}${item.product.images[0]}`
                                    : ""
                                )
                              }
                              />
                              <div
                                className="product-details"
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  className="product-name"
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#333",
                                    width: "200px",
                                    textOverflow: "ellipsis",
                                    overflow: "hidden",
                                  }}
                                >
                                  {item.product.name}
                                </div>
                                <div
                                  className="product-name"
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#333",
                                  }}
                                >
                                  Volumetric: {item.product.volumetricWeight} KG
                                </div>
                                <div
                                  className="product-price"
                                  style={{
                                    fontSize: "14px",
                                    color: "black",
                                  }}
                                >
                                  {formatCurrency(item.product.price)} x{" "}
                                  {item.quantity}
                                </div>
                              </div>
                            </div>
                          ))}
                      </>
                    )}
                  </div>
                </div>

                {/* Footer Section */}
                <div
                  className="order-card-footer p-2 rounded-lg"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <div style={{ width: "300px" }}></div>

                  <div>
                    <strong>Volumetric sum:</strong>{" "}
                    {order.orders
                      ? order.orders
                          .reduce(
                            (sum, item) =>
                              sum +
                              (item.product.volumetricWeight || 0) *
                                item.quantity,
                            0
                          )
                          .toFixed(2)
                      : 0}{" "}
                    KG
                  </div>

                  <div style={{ marginRight: "20px" }}>
                    <strong>Total Qty:</strong>{" "}
                    {order.orders
                      ? order.orders.reduce(
                          (sum, item) => sum + item.quantity,
                          0
                        )
                      : 0}
                  </div>
                </div>

                {/* Price Validation Error */}
                {(() => {
                  const calculatedTotal = order.orders
                    ? order.orders.reduce((sum, item) => {
                        // Apply discount to product price and multiply by quantity
                        const discountedPrice = Math.round(
                          (item.product.price -
                            (item.product.price * item.product.discount) /
                              100) *
                            item.quantity
                        );
                        return sum + discountedPrice;
                      }, 0) +
                      (order.deliveryCharge || 0) +
                      (order.paymentMethod === "cod" ? 50 : 0) -
                      (order.couponDiscount || 0)
                    : 0;

                  if (Math.abs(calculatedTotal - order.totalAmount) > 2) {
                    return (
                      <div
                        style={{
                          backgroundColor: "#ff0000",
                          border: "1px solid #f44336",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          margin: "8px",
                          textAlign: "center",
                          animation:
                            "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                        }}
                      >
                        <span
                          style={{
                            color: "#ffffff",
                            fontWeight: "bold",
                            fontSize: "20px",
                          }}
                        >
                          ERROR: Price mismatch detected
                        </span>
                        <br />
                        {/* <span
                          style={{
                            color: "#d32f2f",
                            fontSize: "12px",
                          }}
                        >
                          Calculated: {formatCurrency(calculatedTotal)} |
                          Stored: {formatCurrency(order.totalAmount)}
                        </span> */}
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            ))}
          </div>
          {fullscreenImageUrl && (
            <div
              role="dialog"
              aria-modal="true"
              className="image-fullscreen-overlay"
              onClick={() => setFullscreenImageUrl(null)}
              style={{
                position: "fixed",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.9)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
                padding: "24px",
              }}
            >
              <img
                src={fullscreenImageUrl}
                alt="Product"
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                }}
              />
              <button
                type="button"
                aria-label="Close"
                onClick={() => setFullscreenImageUrl(null)}
                style={{
                  position: "fixed",
                  top: 12,
                  right: 12,
                  background: "rgba(255,255,255,0.15)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "999px",
                  width: 40,
                  height: 40,
                  cursor: "pointer",
                  fontSize: 24,
                  lineHeight: "40px",
                  textAlign: "center",
                }}
              >
                ×
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Create a component to use with TableContainer
const OrderListCell = ({ row, setSelectedOrders }) => {
  return (
    <OrderList
      orders={[row.original]}
      showCheckbox={false}
      showAddress={true}
      showViewButton={true}
      setSelectedOrders={setSelectedOrders}
    />
  );
};

// Custom Table layout that replaces default TableContainer display
const CustomOrderTable = ({
  data,
  columns,
  isLoading,
  isRefreshing,
  selectedOrders,
  handleCheckboxChange,
  pagination,
  onPageChange,
  handleStatusUpdate,
  setSelectedOrders,
}) => {
  return (
    <div className="custom-order-table">
      <OrderList
        orders={data}
        selectedOrders={selectedOrders}
        handleCheckboxChange={handleCheckboxChange}
        initialLoading={isLoading}
        refreshing={isRefreshing}
        handleStatusUpdate={handleStatusUpdate}
        setSelectedOrders={setSelectedOrders}
      />

      {pagination && (
        <div className="d-flex justify-content-center mt-3">
          <nav className="pagination-container">
            <ul className="pagination flex-wrap">
              <li
                className={`page-item ${
                  pagination.page === 1 ? "disabled" : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
              </li>
              {[
                ...Array(
                  Math.ceil(pagination.totalOrders / pagination.limit)
                ).keys(),
              ].map((i) => (
                <li
                  key={i}
                  className={`page-item ${
                    pagination.page === i + 1 ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => onPageChange(i + 1)}
                  >
                    {i + 1}
                  </button>
                </li>
              ))}
              <li
                className={`page-item ${
                  pagination.page ===
                  Math.ceil(pagination.totalOrders / pagination.limit)
                    ? "disabled"
                    : ""
                }`}
              >
                <button
                  className="page-link"
                  onClick={() => onPageChange(pagination.page + 1)}
                  disabled={
                    pagination.page ===
                    Math.ceil(pagination.totalOrders / pagination.limit)
                  }
                >
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
};

// Original OrdersTable component modified to work with TableContainer
const OrdersTable = (props) => {
  const {
    initialLoading = false,
    refreshing = false,
    orders = [],
    selectedOrders = [],
    handleCheckboxChange = () => {},
    pagination = null,
    onPageChange = () => {},
    handleStatusUpdate = null,
    setSelectedOrders = () => {},
  } = props;

  // For normal usage without TableContainer
  if (!pagination) {
    return (
      <OrderList
        orders={orders}
        selectedOrders={selectedOrders}
        handleCheckboxChange={handleCheckboxChange}
        initialLoading={initialLoading}
        refreshing={refreshing}
        handleStatusUpdate={handleStatusUpdate}
        setSelectedOrders={setSelectedOrders}
      />
    );
  }

  // For use with TableContainer
  return (
    <CustomOrderTable
      data={orders}
      isLoading={initialLoading}
      isRefreshing={refreshing}
      selectedOrders={selectedOrders}
      handleCheckboxChange={handleCheckboxChange}
      setSelectedOrders={setSelectedOrders}
      pagination={pagination}
      onPageChange={onPageChange}
      handleStatusUpdate={handleStatusUpdate}
    />
  );
};

export { OrderList, OrderListCell, CustomOrderTable, OrdersTable };
